import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import { Copy, Trash2, Plus, KeyRound, Smartphone } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENDPOINTS = [
  ["POST", "/auth/login", "Inicia sesión. Body: { email, password } → { token, user }"],
  ["GET", "/auth/me", "Usuario actual"],
  ["GET", "/patients", "Pacientes del doctor"],
  ["POST", "/patients", "Crear paciente"],
  ["GET", "/patients/{id}", "Ficha completa (historial, recetas, citas)"],
  ["GET", "/appointments", "Citas"],
  ["POST", "/appointments", "Crear cita. Body: { patient_id, date (ISO), duration_min, reason }"],
  ["POST", "/prescriptions", "Crear receta"],
  ["GET", "/notifications", "Notificaciones"],
  ["GET", "/chat/contacts", "Contactos del chat"],
  ["POST", "/ai/ask", "Preguntar a la IA. Body: { question, patient_id?, session_id? }"],
  ["GET", "/ai/billing", "Estado de la suscripción de IA"],
  ["POST", "/push/devices", "Registrar teléfono para push. Body: { expo_token, platform: ios|android, device_name? }"],
  ["DELETE", "/push/devices", "Quitar teléfono (al cerrar sesión). Body: { expo_token }"],
  ["GET", "/push/preferences", "Tipos de push activos: referrals, messages, appointments, reminders"],
  ["PUT", "/push/preferences", "Activar/desactivar tipos de push"],
  ["POST", "/push/test", "Enviar push de prueba a mis teléfonos"],
];

const EXPO = `// api.js — Expo / React Native
import AsyncStorage from "@react-native-async-storage/async-storage";

export const API_URL = "${BASE}";
export const API_KEY = "cdk_…"; // la key creada en Admin > API móvil

export async function apiFetch(path, { method = "GET", body } = {}) {
  const token = await AsyncStorage.getItem("token");
  const res = await fetch(API_URL + path, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": API_KEY,
      ...(token ? { Authorization: "Bearer " + token } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.detail || "Error " + res.status);
  return data;
}

export async function login(email, password) {
  const { token, user } = await apiFetch("/auth/login", { method: "POST", body: { email, password } });
  await AsyncStorage.setItem("token", token);
  return user;
}`;

const copy = (t, label = "Copiado") => navigator.clipboard.writeText(t).then(() => toast.success(label));

export default function ApiMobile() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null);

  const load = () => api.get("/admin/api-keys").then(({ data }) => setKeys(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/api-keys", { name: name.trim() || "App móvil" });
      setCreated(data);
      setName("");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };
  const revoke = async (k) => {
    if (!window.confirm(`¿Revocar la key "${k.name}"? La app que la use dejará de funcionar.`)) return;
    await api.delete(`/admin/api-keys/${k.id}`).catch(() => {});
    toast.success("Key revocada");
    load();
  };
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : "Nunca");

  return (
    <div className="space-y-6" data-testid="admin-api-mobile">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-black">API para app móvil</h1>
        <p className="text-[14px] text-black/55 mt-1">La app móvil usa la misma API y la misma base de datos que la web.</p>
      </div>

      <div className="cai-panel relative overflow-hidden rounded-[28px] p-5 text-white">
        <div className="relative z-10 space-y-3">
          <div className="flex items-center gap-2 text-[13px] font-semibold"><Smartphone className="w-4 h-4" /> URL base de la API</div>
          <div className="flex items-center gap-2 bg-white/15 border border-white/30 rounded-full pl-4 pr-1.5 h-11">
            <code className="flex-1 text-[13px] truncate" data-testid="api-base-url">{BASE}</code>
            <button type="button" onClick={() => copy(BASE, "URL copiada")} data-testid="api-copy-base" className="h-8 px-3 rounded-full bg-white text-[#5B8FD0] text-[12px] font-semibold inline-flex items-center gap-1">
              <Copy className="w-3.5 h-3.5" /> Copiar
            </button>
          </div>
          <div className="text-[12px] text-white/85">
            Envía en cada petición <code>X-API-Key: cdk_…</code> y, después de iniciar sesión, <code>Authorization: Bearer &lt;token&gt;</code>.
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[28px] border border-black/[0.06] p-5 space-y-4">
        <div className="flex items-center gap-2 text-[15px] font-semibold text-black"><KeyRound className="w-4 h-4" /> API keys</div>
        <form onSubmit={create} className="flex gap-2">
          <input
            value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre (ej. App iOS producción)"
            data-testid="api-key-name"
            className="flex-1 h-11 px-4 rounded-full border border-black/[0.08] text-[14px]"
          />
          <button type="submit" data-testid="api-key-create" className="h-11 px-5 rounded-full bg-black text-white text-[13px] font-semibold inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Crear key
          </button>
        </form>
        {created && (
          <div className="rounded-[20px] bg-[#6FA2DE]/10 border border-[#6FA2DE]/30 p-4" data-testid="api-key-created">
            <div className="text-[13px] font-semibold text-black">Copia esta key ahora: no se volverá a mostrar.</div>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 text-[12.5px] break-all bg-white rounded-xl px-3 py-2 border border-black/[0.06]" data-testid="api-key-value">{created.key}</code>
              <button type="button" onClick={() => copy(created.key, "Key copiada")} className="h-9 px-3 rounded-full bg-black text-white text-[12px] font-semibold inline-flex items-center gap-1">
                <Copy className="w-3.5 h-3.5" /> Copiar
              </button>
            </div>
          </div>
        )}
        <div className="divide-y divide-black/[0.05]">
          {keys.length === 0 && <div className="text-[13px] text-black/50 py-2">Aún no hay keys.</div>}
          {keys.map((k) => (
            <div key={k.id} className="py-3 flex items-center gap-3" data-testid={`api-key-row-${k.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-black truncate">
                  {k.name} <code className="ml-1 text-[11.5px] font-normal text-black/45">{k.prefix}…</code>
                </div>
                <div className="text-[11.5px] text-black/50">Creada {fmt(k.created_at)} · Último uso: {fmt(k.last_used_at)}</div>
              </div>
              {k.revoked ? (
                <span className="text-[11px] font-semibold px-2.5 h-6 inline-flex items-center rounded-full bg-black/[0.06] text-black/50">Revocada</span>
              ) : (
                <button type="button" onClick={() => revoke(k)} data-testid={`api-key-revoke-${k.id}`} className="h-8 px-3 rounded-full bg-[#FF3B30]/10 text-[#D70015] text-[12px] font-semibold inline-flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Revocar
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-[28px] border border-black/[0.06] p-5 space-y-3">
        <div className="text-[15px] font-semibold text-black">Endpoints principales</div>
        <div className="divide-y divide-black/[0.05]">
          {ENDPOINTS.map(([m, p, d]) => (
            <div key={m + p} className="py-2 flex items-center gap-3 text-[13px]">
              <span className={`w-14 shrink-0 text-[11px] font-bold ${m === "GET" ? "text-[#15803D]" : "text-[#3B6FB0]"}`}>{m}</span>
              <code className="w-48 shrink-0 text-black">{p}</code>
              <span className="text-black/55">{d}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-[28px] border border-black/[0.06] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[15px] font-semibold text-black">Ejemplo para Expo / React Native</div>
          <button type="button" onClick={() => copy(EXPO, "Código copiado")} data-testid="api-copy-code" className="h-8 px-3 rounded-full bg-black/[0.05] text-[12px] font-semibold inline-flex items-center gap-1">
            <Copy className="w-3.5 h-3.5" /> Copiar código
          </button>
        </div>
        <pre className="text-[12px] leading-relaxed bg-[#0F172A] text-[#E2E8F0] rounded-[20px] p-4 overflow-x-auto">{EXPO}</pre>
      </div>
    </div>
  );
}
